/* A searchable currency picker.
 *
 * The markup and class names are exactly those in
 * `design/components/currency-select` and `design/components/person-picker` —
 * the mockup is the specification, and keeping them identical is what lets the
 * two be diffed. What React adds is the wiring the mockup could only show
 * statically: which option is active, what the live region says, and what the
 * field reports to the form.
 *
 * `design.md` prefers a real `<select>` and permits a custom control only where
 * the behaviour genuinely requires it. This one offers every ISO 4217 code the
 * runtime knows — about 160 — at the one moment an Organisation has no enabled
 * set yet, so it is searched rather than scanned. The everyday picker over the
 * five enabled values stays a `<select>`; that is STORY-041.
 *
 * Three rules the implementation must keep, and they are the ones that break
 * quietly:
 *
 *  1. **Keyboard focus stays in the input.** The active option is pointed at
 *     with `aria-activedescendant`, never with `.focus()`. Moving real focus
 *     into the list breaks typing, which is the whole interaction.
 *  2. **The group headers are not options.** They carry `role="presentation"`,
 *     so nothing arrows onto "Common" and picks it by accident.
 *  3. **Nothing is active until an arrow key says so.** Opening with the first
 *     option pre-selected means Enter picks a currency nobody chose, and this
 *     is the one field on the create form that cannot be changed later.
 *
 * Every string arrives as a prop, as `TextField`'s do: the shared UI layer holds
 * no catalogue keys. The locale is the exception, and it is not copy — it
 * decides how names are collated and how the search folds case, which in
 * Turkish is the difference between finding the Iraqi Dinar and not.
 */

import { type CurrencyOption, filterCurrencies, listCurrencies } from '@eventuley/i18n';
import type { KeyboardEvent, ReactNode } from 'react';
import { Fragment, useId, useMemo, useState } from 'react';
import { useI18n } from '../i18n/index.js';

/** No option is active. Opening the list does not choose anything. */
const NONE_ACTIVE = -1;

export interface CurrencySelectProps {
  readonly label: string;
  /** Heading over the codes a new workspace starts with. */
  readonly commonLabel: string;
  /** Heading over everything else. */
  readonly allLabel: string;
  /** Shown and announced when the query matches nothing. */
  readonly emptyLabel: string;
  /** Announced as the list narrows. Takes the match count. */
  readonly resultsLabel: (count: number) => string;
  readonly hint?: ReactNode;
  /** Present means the field is in its error state; absent means it is not. */
  readonly error?: string | undefined;
  /** The chosen ISO 4217 code, or an empty string. */
  readonly value: string;
  readonly onChange: (code: string) => void;
}

export function CurrencySelect({
  label,
  commonLabel,
  allLabel,
  emptyLabel,
  resultsLabel,
  hint,
  error,
  value,
  onChange,
}: CurrencySelectProps) {
  const { locale } = useI18n();
  const id = useId();
  const listId = `${id}-list`;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const statusId = `${id}-status`;

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(NONE_ACTIVE);

  const { common, all } = useMemo(() => listCurrencies(locale), [locale]);

  const searching = query.trim() !== '';
  const matches = useMemo(
    () => (searching ? filterCurrencies(all, query, locale) : all),
    [all, query, locale, searching],
  );

  /* A blank query shows both groups; a query searches everything and the groups
   * collapse, because one of the five matching would otherwise appear twice. */
  const groups: readonly {
    readonly heading: string | null;
    readonly options: readonly CurrencyOption[];
  }[] = searching
    ? [{ heading: null, options: matches }]
    : [
        { heading: commonLabel, options: common },
        { heading: allLabel, options: all },
      ];

  /* One flat list in render order, so the arrow keys and the group boundaries
   * cannot disagree about what is third. */
  const flat = groups.flatMap((group) => group.options);
  const activeOption = activeIndex === NONE_ACTIVE ? undefined : flat[activeIndex];

  const selected = useMemo(() => all.find((option) => option.code === value), [all, value]);
  const selectedLabel = selected ? `${selected.code} — ${selected.name}` : '';
  /* While open, the field shows the query — but only ONCE there is one.
   * Showing an empty box the moment a filled field takes focus reads as the
   * value having been thrown away: pick USD, tab out, tab back, and both the
   * visible text and the accessible value were empty until blur. */
  const displayValue = open ? (query === '' ? selectedLabel : query) : selectedLabel;

  const optionId = (index: number) => `${id}-option-${index}`;

  function close() {
    setOpen(false);
    setQuery('');
    setActiveIndex(NONE_ACTIVE);
  }

  function commit(option: CurrencyOption | undefined) {
    if (!option) return;
    onChange(option.code);
    close();
  }

  function move(step: 1 | -1) {
    if (flat.length === 0) return;
    setActiveIndex((index) => {
      if (index === NONE_ACTIVE) return step === 1 ? 0 : flat.length - 1;
      /* Wraps rather than stopping. Falling off the end of a 160-item list with
       * nothing happening reads as a control that has stopped responding. */
      return (index + step + flat.length) % flat.length;
    });
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      move(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if (event.key === 'Home' || event.key === 'End') {
      if (!open || flat.length === 0) return;
      event.preventDefault();
      setActiveIndex(event.key === 'Home' ? 0 : flat.length - 1);
      return;
    }

    if (event.key === 'Enter') {
      /* Only swallowed when it is actually choosing something. With nothing
       * active, Enter still submits the form, which is what somebody who has
       * already picked a currency expects. */
      if (!open || !activeOption) return;
      event.preventDefault();
      commit(activeOption);
      return;
    }

    if (event.key === 'Escape') {
      if (!open) return;
      event.preventDefault();
      /* Abandons the search; it does not clear the field. Losing a chosen value
       * because somebody dismissed a list is a small betrayal. */
      close();
    }
  }

  const describedBy = [hint ? hintId : null, error ? errorId : null, open ? statusId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="picker">
      <div className="field">
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
        <input
          id={id}
          className="field__control"
          type="text"
          role="combobox"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          {...(activeOption ? { 'aria-activedescendant': optionId(activeIndex) } : {})}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          value={displayValue}
          onChange={(event) => {
            /* The first keystroke replaces the label rather than appending to
             * it: the field was showing a selection, not a partial query. */
            setQuery(
              query === '' ? event.target.value.replace(selectedLabel, '') : event.target.value,
            );
            setOpen(true);
            /* A new query means the old position means nothing. */
            setActiveIndex(NONE_ACTIVE);
          }}
          onFocus={() => setOpen(true)}
          /* Closing on blur rather than on a document click: the list has no
           * focusable children, so leaving the input is the only way out. */
          onBlur={() => close()}
          onKeyDown={onKeyDown}
        />
        {hint ? (
          <p className="field__hint" id={hintId}>
            {hint}
          </p>
        ) : null}
        {error ? (
          /* `role="alert"` so it is announced when it appears. */
          <p className="field__error" id={errorId} role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <ul
        className="picker__list"
        id={listId}
        // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: a listbox on a <ul> IS the ARIA combobox pattern, and it is the markup design/components/person-picker documents. The rule is aimed at a <ul> quietly made clickable; this one is announced as a listbox because it is one.
        role="listbox"
        aria-label={label}
        hidden={!open || flat.length === 0}
      >
        {groups.map((group, groupIndex) => {
          /* Where this group's options start in the flat list. */
          const offset = groups
            .slice(0, groupIndex)
            .reduce((total, earlier) => total + earlier.options.length, 0);
          const groupKey = group.heading ?? 'matches';

          return (
            <Fragment key={groupKey}>
              {group.heading ? (
                <li className="picker__group" role="presentation">
                  {group.heading}
                </li>
              ) : null}
              {group.options.map((option, index) => {
                const position = offset + index;
                return (
                  // biome-ignore lint/a11y/useFocusableInteractive: options in an aria-activedescendant combobox must NOT be focusable. Focus stays in the input and the active option is pointed at by id; a tabindex here would break the very interaction the rule protects.
                  <li
                    key={`${groupKey}-${option.code}`}
                    className="picker__option"
                    id={optionId(position)}
                    // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: an option on an <li> inside a listbox is the pattern, not a violation of it.
                    role="option"
                    aria-selected={position === activeIndex}
                    {...(position === activeIndex ? { 'data-active': 'true' } : {})}
                    /* `onMouseDown` rather than `onClick`: the input's blur
                     * fires first otherwise and closes the list out from under
                     * the pointer. `preventDefault` is what keeps focus in the
                     * input, which rule 1 above requires. */
                    onMouseDown={(event) => {
                      event.preventDefault();
                      commit(option);
                    }}
                  >
                    <span className="picker__main">
                      <span className="picker__code">{option.code}</span>
                      <span className="picker__name">{option.name}</span>
                    </span>
                  </li>
                );
              })}
            </Fragment>
          );
        })}
      </ul>

      {open ? (
        <p className="picker__status" id={statusId} role="status">
          {flat.length === 0 ? emptyLabel : resultsLabel(flat.length)}
        </p>
      ) : null}
    </div>
  );
}
